/**
 * The filter builder's model (ADR 0020, FRONTEND_DESIGN §4.13): a filter AST read into groups of rows and written
 * back. A group matches all, any or none of its items (`and`, `or`, `not` of an `or`); a row is field · comparison ·
 * value; a part that is not a row (an unusual shape, a field the builder does not know) stays as JSON text, so
 * nothing is lost on the way through.
 */
import type { DayRef, FilterAst } from "../core/filter";

export type Mode = "all" | "any" | "none";
export interface Group {
  kind: "group";
  id: string;
  mode: Mode;
  items: Item[];
}
export interface Row {
  kind: "row";
  id: string;
  /** done · archived · type · text · group · seq · parent · prop:<id> · signal:<name> */
  field: string;
  op: string;
  value?: unknown;
}
export interface Raw {
  kind: "raw";
  id: string;
  text: string;
}
export type Item = Group | Row | Raw;

let seq = 0;
export const newId = () => `f${++seq}`;
export const emptyGroup = (mode: Mode = "all"): Group => ({ kind: "group", id: newId(), mode, items: [] });

/** What a property field compares: its kind decides the comparisons offered. */
export type FieldKind = "flag" | "enum" | "multi" | "number" | "date" | "text" | "bool" | "geo" | "signal";

const VALUE_OPS = ["eq", "neq", "gt", "lt", "gte", "lte", "in", "has"] as const;
const DATE_ONE = ["before", "after", "on", "onOrBefore", "onOrAfter"] as const;
/** comparisons that need no value */
export const NO_VALUE = new Set(["set", "notSet", "yes", "no", "top"]);

const raw = (ast: unknown): Raw => ({ kind: "raw", id: newId(), text: JSON.stringify(ast) });
const row = (field: string, op: string, value?: unknown): Row => ({
  kind: "row",
  id: newId(),
  field,
  op,
  value,
});
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const isDays = (x: unknown, sign: 1 | -1 | 0): number | null =>
  isObj(x) && typeof x.days === "number" && Object.keys(x).length === 1 && Math.sign(x.days) === sign
    ? x.days
    : null;

/** One leaf as a row, or null when it is not one the builder shows as a row. */
function leafRow(a: Record<string, unknown>, boolKeys: Set<string>): Row | null {
  const keys = Object.keys(a);
  const only = (k: string) => keys.length === 1 && k in a;
  if (only("done") && typeof a.done === "boolean") return row("done", "is", a.done);
  if (only("archived") && typeof a.archived === "boolean") return row("archived", "is", a.archived);
  if (only("type") && typeof a.type === "string") return row("type", "eq", a.type);
  if (only("text") && typeof a.text === "string") return row("text", "contains", a.text);
  if (only("group") && typeof a.group === "string") return row("group", "member", a.group);
  if (only("seq") && typeof a.seq === "string") return row("seq", "member", a.seq);
  if (only("parent")) return a.parent === null ? row("parent", "top") : row("parent", "eq", a.parent);
  if (only("tag") && typeof a.tag === "string") return row(`prop:${a.tag}`, "yes");
  if (typeof a.prop === "string" && keys.length <= 2) {
    const field = `prop:${a.prop}`;
    const op = keys.find((k) => k !== "prop");
    if (!op) return row(field, "set");
    if (boolKeys.has(a.prop) && op === "eq" && typeof a.eq === "boolean")
      return row(field, a.eq ? "yes" : "no");
    if ((VALUE_OPS as readonly string[]).includes(op)) return row(field, op, a[op]);
    if ((DATE_ONE as readonly string[]).includes(op)) return row(field, op, a[op]);
    if (op === "between" && Array.isArray(a.between) && a.between.length === 2) {
      const [lo, hi] = a.between;
      const next = isDays(hi, 1);
      if (isDays(lo, 0) === 0 || (isObj(lo) && lo.days === 0)) {
        if (next !== null) return row(field, "next", next);
      }
      const last = isDays(lo, -1);
      if (last !== null && isObj(hi) && hi.days === 0) return row(field, "last", -last);
      return row(field, "between", [lo, hi]);
    }
    return null;
  }
  if (typeof a.signal === "string" && keys.length === 2) {
    const op = keys.find((k) => k !== "signal")!;
    if ((VALUE_OPS as readonly string[]).includes(op)) return row(`signal:${a.signal}`, op, a[op]);
  }
  return null;
}

/** The negated form of a row, where one exists ("is" → "is not", "is set" → "is not set" …). */
function negate(r: Row): Row | null {
  const flip: Record<string, string> = {
    eq: "neq",
    neq: "eq",
    in: "notIn",
    contains: "notContains",
    set: "notSet",
    yes: "no",
  };
  if (r.field === "done" || r.field === "archived") return { ...r, value: !r.value };
  if (r.field === "group" || r.field === "seq") return r.op === "member" ? { ...r, op: "notMember" } : null;
  if (r.field === "parent") return r.op === "eq" ? { ...r, op: "neq" } : null;
  const op = flip[r.op];
  return op ? { ...r, op } : null;
}

function parseItem(ast: unknown, boolKeys: Set<string>): Item {
  if (!isObj(ast)) return raw(ast);
  if (Array.isArray(ast.and) && Object.keys(ast).length === 1)
    return { kind: "group", id: newId(), mode: "all", items: ast.and.map((a) => parseItem(a, boolKeys)) };
  if (Array.isArray(ast.or) && Object.keys(ast).length === 1)
    return { kind: "group", id: newId(), mode: "any", items: ast.or.map((a) => parseItem(a, boolKeys)) };
  if ("not" in ast && Object.keys(ast).length === 1) {
    const inner = ast.not;
    if (isObj(inner) && Array.isArray(inner.or) && Object.keys(inner).length === 1)
      return { kind: "group", id: newId(), mode: "none", items: inner.or.map((a) => parseItem(a, boolKeys)) };
    if (isObj(inner)) {
      if (typeof inner.prop === "string" && Object.keys(inner).length === 1)
        return row(`prop:${inner.prop}`, "notSet");
      if (typeof inner.tag === "string" && Object.keys(inner).length === 1)
        return row(`prop:${inner.tag}`, "no");
      if (typeof inner.prop === "string" && Array.isArray(inner.in) && Object.keys(inner).length === 2)
        return row(`prop:${inner.prop}`, "notIn", inner.in);
      if (typeof inner.text === "string" && Object.keys(inner).length === 1)
        return row("text", "notContains", inner.text);
      if (typeof inner.type === "string" && Object.keys(inner).length === 1)
        return row("type", "neq", inner.type);
      const r = leafRow(inner, boolKeys);
      const n = r && negate(r);
      if (n) return n;
    }
    return { kind: "group", id: newId(), mode: "none", items: [parseItem(inner, boolKeys)] };
  }
  return leafRow(ast, boolKeys) ?? raw(ast);
}

/**
 * A filter as the builder's top group. `boolKeys`: computed yes/no values (blocked, active), whose `eq: true/false`
 * reads as yes/no.
 */
export function parse(ast: FilterAst | null | undefined, boolKeys: Set<string> = new Set()): Group {
  if (!ast || (isObj(ast) && Object.keys(ast).length === 0)) return emptyGroup();
  const item = parseItem(ast, boolKeys);
  return item.kind === "group" ? item : { kind: "group", id: newId(), mode: "all", items: [item] };
}

/** Whether a row has what it needs to be written. */
export function rowComplete(r: Row): boolean {
  if (NO_VALUE.has(r.op)) return true;
  const v = r.value;
  if (r.op === "in" || r.op === "notIn" || r.op === "anyOf") return Array.isArray(v) && v.length > 0;
  if (r.op === "between") return Array.isArray(v) && v.length === 2 && v.every((x) => dayRefOk(x));
  if (r.op === "next" || r.op === "last") return typeof v === "number" && v >= 0;
  if ((DATE_ONE as readonly string[]).includes(r.op)) return dayRefOk(v);
  if (typeof v === "number") return Number.isFinite(v);
  return v !== undefined && v !== null && v !== "";
}

export function dayRefOk(x: unknown): x is DayRef {
  return (typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x)) || (isObj(x) && typeof x.days === "number");
}

function rowAst(r: Row, tagKeys: Set<string>): FilterAst | null {
  if (!rowComplete(r)) return null;
  const not = (a: FilterAst): FilterAst => ({ not: a });
  const v = r.value;
  switch (r.field) {
    case "done":
      return { done: Boolean(v) };
    case "archived":
      return { archived: Boolean(v) };
    case "type":
      return r.op === "neq" ? not({ type: String(v) }) : { type: String(v) };
    case "text":
      return r.op === "notContains" ? not({ text: String(v) }) : { text: String(v) };
    case "group":
    case "seq": {
      const k = r.field;
      const one = (id: string) => (k === "group" ? { group: id } : { seq: id });
      if (r.op === "notMember") return not(one(String(v)));
      if (r.op === "anyOf" && Array.isArray(v))
        return v.length === 1 ? one(String(v[0])) : { or: v.map((x) => one(String(x))) };
      return one(String(v));
    }
    case "parent":
      return r.op === "top"
        ? { parent: null }
        : r.op === "neq"
          ? not({ parent: String(v) })
          : { parent: String(v) };
  }
  if (r.field.startsWith("signal:")) return { signal: r.field.slice(7), [r.op]: v } as FilterAst;
  const prop = r.field.slice(5);
  switch (r.op) {
    case "set":
      return { prop };
    case "notSet":
      return not({ prop });
    case "yes":
      return tagKeys.has(prop) ? { tag: prop } : ({ prop, eq: true } as FilterAst);
    case "no":
      return tagKeys.has(prop) ? not({ tag: prop }) : ({ prop, eq: false } as FilterAst);
    case "notIn":
      return not({ prop, in: v as unknown[] } as FilterAst);
    case "next":
      return { prop, between: [{ days: 0 }, { days: Number(v) }] } as FilterAst;
    case "last":
      return { prop, between: [{ days: -Number(v) }, { days: 0 }] } as FilterAst;
    default:
      return { prop, [r.op]: v } as FilterAst;
  }
}

function itemAst(it: Item, tagKeys: Set<string>): FilterAst | null | "invalid" {
  if (it.kind === "raw") {
    try {
      return JSON.parse(it.text) as FilterAst;
    } catch {
      return "invalid";
    }
  }
  if (it.kind === "row") return rowAst(it, tagKeys) ?? "invalid";
  const parts: FilterAst[] = [];
  for (const x of it.items) {
    const a = itemAst(x, tagKeys);
    if (a === "invalid") return "invalid";
    if (a) parts.push(a);
  }
  if (!parts.length) return null;
  const either = parts.length === 1 ? parts[0] : { or: parts };
  if (it.mode === "none") return { not: either };
  if (it.mode === "any") return either;
  return parts.length === 1 ? parts[0] : { and: parts };
}

/**
 * The group as a filter: undefined for "every task", "invalid" while a row is unfinished or JSON text does not
 * parse. `tagKeys`: properties of kind tag (yes/no is written as `{tag}`).
 */
export function toAst(g: Group, tagKeys: Set<string> = new Set()): FilterAst | undefined | "invalid" {
  const a = itemAst(g, tagKeys);
  return a === null ? undefined : a;
}
